import { useEffect, useMemo, useState } from "react";
import "./adminPanel.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const ADMIN_TOKEN_STORAGE_KEY = "super_daddy_admin_token";
const ADMIN_USER_STORAGE_KEY = "super_daddy_admin_user";

async function apiRequest(path, options = {}, token = "") {
  const headers = {
    ...(options.headers || {}),
  };

  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed: ${res.status}`);
  }
  return data.data;
}

async function apiGet(path, token) {
  return apiRequest(path, { method: "GET" }, token);
}

async function apiPost(path, body, token) {
  return apiRequest(
    path,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    token,
  );
}

function buildAnalyticsQuery(filter) {
  const p = new URLSearchParams();
  p.set("mode", filter.mode);

  if (
    filter.mode === "year" ||
    filter.mode === "quarter" ||
    filter.mode === "month"
  ) {
    p.set("year", String(filter.year));
  }
  if (filter.mode === "quarter") p.set("quarter", String(filter.quarter));
  if (filter.mode === "month") p.set("month", String(filter.month));
  if (filter.mode === "day") p.set("date", filter.date);

  return `?${p.toString()}`;
}

function AdminPanel() {
  const todayStr = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const [filter, setFilter] = useState({
    mode: "total", // total|year|quarter|month|day
    year: new Date().getFullYear(),
    quarter: 1,
    month: new Date().getMonth() + 1,
    date: todayStr,
  });

  const [playersSeries, setPlayersSeries] = useState([]);
  const [bySet, setBySet] = useState([]);
  const [sessionToken, setSessionToken] = useState(
    () => localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || "",
  );
  const [currentUser, setCurrentUser] = useState(() => {
    const raw = localStorage.getItem(ADMIN_USER_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  });
  const [checkingSession, setCheckingSession] = useState(true);
  const [loginForm, setLoginForm] = useState({
    username: "",
    password: "",
  });

  const [recordsQuery, setRecordsQuery] = useState({
    from: "",
    to: "",
    questionSetRange: "all", // all | 0-3 | 4-6 | 7+
    search: "",
    page: 1,
    pageSize: 20,
  });

  const [records, setRecords] = useState({
    rows: [],
    total: 0,
    page: 1,
    pageSize: 20,
  });
  const [selected, setSelected] = useState(null); // { recordId, record, answers }

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function loadAnalytics(token, filterValue = filter) {
    const q = buildAnalyticsQuery(filterValue);
    const [p, s] = await Promise.all([
      apiGet(`/admin/analytics/players${q}`, token),
      apiGet(`/admin/analytics/by-question-set${q}`, token),
    ]);
    setPlayersSeries(p.series || []);
    setBySet(s.items || []);
  }

  async function loadRecords(token, query = recordsQuery) {
    const p = new URLSearchParams();
    if (query.from) p.set("from", query.from);
    if (query.to) p.set("to", query.to);

    if (query.questionSetRange && query.questionSetRange !== "all") {
      const map = { "0-3": 1, "4-6": 2, "7+": 3 };
      const id = map[query.questionSetRange];
      if (id) p.set("questionSetId", String(id));
    }

    if (query.search) p.set("search", query.search);
    p.set("page", String(query.page));
    p.set("pageSize", String(query.pageSize));

    const data = await apiGet(`/admin/records?${p.toString()}`, token);
    setRecords(data);
  }

  useEffect(() => {
    let cancelled = false;

    async function verifySession() {
      if (!sessionToken) {
        setCheckingSession(false);
        return;
      }

      try {
        const data = await apiGet("/admin/me", sessionToken);
        if (cancelled) return;
        setCurrentUser(data.user || null);
        localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, sessionToken);
        localStorage.setItem(
          ADMIN_USER_STORAGE_KEY,
          JSON.stringify(data.user || null),
        );
      } catch {
        if (cancelled) return;
        localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        localStorage.removeItem(ADMIN_USER_STORAGE_KEY);
        setSessionToken("");
        setCurrentUser(null);
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    }

    verifySession();

    return () => {
      cancelled = true;
    };
  }, [sessionToken]);

  useEffect(() => {
    if (!sessionToken || !currentUser) return;

    let cancelled = false;
    setLoading(true);
    setErr("");
    Promise.all([loadAnalytics(sessionToken), loadRecords(sessionToken)])
      .catch((e) => {
        if (!cancelled) setErr(e.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionToken,
    currentUser,
    filter.mode,
    filter.year,
    filter.quarter,
    filter.month,
    filter.date,
    recordsQuery.page,
    recordsQuery.pageSize,
  ]);

  async function onSearchSubmit(e) {
    e.preventDefault();
    const nextQuery = { ...recordsQuery, page: 1 };
    setRecordsQuery(nextQuery);
    setLoading(true);
    setErr("");
    try {
      await loadRecords(sessionToken, nextQuery);
    } catch (e2) {
      setErr(e2.message || String(e2));
    } finally {
      setLoading(false);
    }
  }

  async function onLoginSubmit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await apiPost("/admin/login", loginForm);
      localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, data.token);
      localStorage.setItem(ADMIN_USER_STORAGE_KEY, JSON.stringify(data.user));
      setSessionToken(data.token);
      setCurrentUser(data.user);
      setLoginForm({ username: "", password: "" });
    } catch (e2) {
      localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ADMIN_USER_STORAGE_KEY);
      setSessionToken("");
      setCurrentUser(null);
      setErr(e2.message || String(e2));
    } finally {
      setLoading(false);
      setCheckingSession(false);
    }
  }

  async function onLogout() {
    if (!sessionToken) return;

    setErr("");
    setLoading(true);
    try {
      await apiPost("/admin/logout", {}, sessionToken);
    } catch {
      // Ignore logout errors and clear local session anyway.
    } finally {
      localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ADMIN_USER_STORAGE_KEY);
      setSessionToken("");
      setCurrentUser(null);
      setLoading(false);
    }
  }

  async function openRecord(recordId) {
    setErr("");
    setLoading(true);
    try {
      const data = await apiGet(`/admin/records/${recordId}`, sessionToken);
      setSelected(data);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  function downloadExport() {
    const q = buildAnalyticsQuery(filter);

    const p = new URLSearchParams(q.startsWith("?") ? q.slice(1) : q);

    if (recordsQuery.from) p.set("from", recordsQuery.from);
    if (recordsQuery.to) p.set("to", recordsQuery.to);

    if (
      recordsQuery.questionSetRange &&
      recordsQuery.questionSetRange !== "all"
    ) {
      const map = { "0-3": 1, "4-6": 2, "7+": 3 };
      const id = map[recordsQuery.questionSetRange];
      if (id) p.set("questionSetId", String(id));
    }

    // Use a normal navigation download so browser saves the file.
    const url = `${API_BASE}/admin/export.xlsx?${p.toString()}`;

    // Download with the current admin session token.
    fetch(url, {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "super-daddy-admin-export.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => setErr(e.message || String(e)));
  }

  const bucketLabel =
    filter.mode === "month" ? "日" : filter.mode === "day" ? "時" : "月";

  if (checkingSession) {
    return (
      <main className="admin-panel ap-auth-shell">
        <section className="ap-card ap-auth-card">
          <h1>Admin Panel</h1>
          <p>Checking session…</p>
        </section>
      </main>
    );
  }

  if (!sessionToken || !currentUser) {
    return (
      <main className="admin-panel ap-auth-shell">
        <section className="ap-card ap-auth-card">
          <h1>Admin Login</h1>

          {err ? <div className="ap-error">{err}</div> : null}

          <form className="ap-login-form" onSubmit={onLoginSubmit}>
            <label>
              Username
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) =>
                  setLoginForm((f) => ({ ...f, username: e.target.value }))
                }
                autoComplete="username"
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) =>
                  setLoginForm((f) => ({ ...f, password: e.target.value }))
                }
                autoComplete="current-password"
              />
            </label>
            <button className="ap-btn" type="submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-panel">
      <header className="ap-header">
        <div>
          <h1>Admin Panel</h1>
          <div className="ap-subtitle">
            Signed in as {currentUser.displayName || currentUser.username}
          </div>
        </div>

        <div className="ap-actions">
          <button className="ap-btn ap-btn-secondary" onClick={onLogout}>
            Logout
          </button>
          <button className="ap-btn" onClick={downloadExport}>
            Export to Excel
          </button>
        </div>
      </header>

      {err ? <div className="ap-error">{err}</div> : null}
      {loading ? <div className="ap-loading">Loading…</div> : null}

      <section className="ap-card">
        <h2>Filters</h2>
        <div className="ap-filters">
          <label>
            <select
              value={filter.mode}
              onChange={(e) =>
                setFilter((f) => ({ ...f, mode: e.target.value }))
              }
            >
              <option value="total">總累計</option>
              <option value="year">年報表</option>
              <option value="quarter">季報表</option>
              <option value="month">月報表</option>
              <option value="day">日報表</option>
            </select>
          </label>

          {(filter.mode === "year" ||
            filter.mode === "quarter" ||
            filter.mode === "month") && (
            <label>
              Year
              <input
                type="number"
                value={filter.year}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, year: Number(e.target.value) }))
                }
              />
            </label>
          )}

          {filter.mode === "quarter" && (
            <label>
              Quarter
              <select
                value={filter.quarter}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, quarter: Number(e.target.value) }))
                }
              >
                <option value={1}>Q1</option>
                <option value={2}>Q2</option>
                <option value={3}>Q3</option>
                <option value={4}>Q4</option>
              </select>
            </label>
          )}

          {filter.mode === "month" && (
            <label>
              Month
              <select
                value={filter.month}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, month: Number(e.target.value) }))
                }
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          )}

          {filter.mode === "day" && (
            <label>
              Date
              <input
                type="date"
                value={filter.date}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, date: e.target.value }))
                }
              />
            </label>
          )}
        </div>
      </section>

      <section className="ap-grid">
        <div className="ap-card">
          <h2>每{bucketLabel}玩家總人數</h2>
          <div className="ap-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={playersSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="plays" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="ap-card">
          <h2>每份問卷玩家人數</h2>
          <div className="ap-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={bySet}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="questionSetName" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="plays" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="ap-card">
        <h2>所有詳細分析記錄</h2>

        <form className="ap-record-filters" onSubmit={onSearchSubmit}>
          <label>
            From
            <input
              type="date"
              value={recordsQuery.from}
              onChange={(e) =>
                setRecordsQuery((q) => ({ ...q, from: e.target.value }))
              }
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={recordsQuery.to}
              onChange={(e) =>
                setRecordsQuery((q) => ({ ...q, to: e.target.value }))
              }
            />
          </label>
          <label>
            Question Set
            <select
              value={recordsQuery.questionSetRange}
              onChange={(e) =>
                setRecordsQuery((q) => ({
                  ...q,
                  questionSetRange: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="all">All</option>
              <option value="0-3">0 - 3</option>
              <option value="4-6">4 - 6</option>
              <option value="7+">7+</option>
            </select>
          </label>
          <label>
            Search (name/mail/phone)
            <input
              type="text"
              value={recordsQuery.search}
              onChange={(e) =>
                setRecordsQuery((q) => ({ ...q, search: e.target.value }))
              }
            />
          </label>
          <button className="ap-btn" type="submit">
            Apply
          </button>
        </form>

        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead>
              <tr>
                <th>DateTime</th>
                <th>Questionnaire</th>
                <th>Name</th>
                <th>Children</th>
                <th>Mail</th>
                <th>Phone</th>
                <th>Total</th>
                <th>Subscribe</th>
                <th>Follow-up</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {records.rows.map((r) => (
                <tr key={r.recordId}>
                  <td>{String(r.datetime)}</td>
                  <td>
                    {r.questionSetName} (#{r.questionSetId})
                  </td>
                  <td>{r.name || "-"}</td>
                  <td>{r.childrenCount || "-"}</td>
                  <td>{r.mail || "-"}</td>
                  <td>{r.phone || "-"}</td>
                  <td>{r.questionSetScore}</td>
                  <td>{String(r.subscribe)}</td>
                  <td>{String(r.followUpRequired)}</td>
                  <td>
                    <button
                      className="ap-link"
                      type="button"
                      onClick={() => openRecord(r.recordId)}
                    >
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
              {records.rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="ap-empty">
                    No records
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="ap-pagination">
          <button
            className="ap-btn"
            disabled={records.page <= 1}
            onClick={() => setRecordsQuery((q) => ({ ...q, page: q.page - 1 }))}
          >
            Prev
          </button>
          <div className="ap-pageinfo">
            Page {records.page} /{" "}
            {Math.max(1, Math.ceil(records.total / records.pageSize))} (Total{" "}
            {records.total})
          </div>
          <button
            className="ap-btn"
            disabled={records.page * records.pageSize >= records.total}
            onClick={() => setRecordsQuery((q) => ({ ...q, page: q.page + 1 }))}
          >
            Next
          </button>

          <label className="ap-pagesize">
            Page size
            <select
              value={records.pageSize}
              onChange={(e) =>
                setRecordsQuery((q) => ({
                  ...q,
                  pageSize: Number(e.target.value),
                  page: 1,
                }))
              }
            >
              {[10, 20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {selected ? (
        <section
          className="ap-modal-backdrop"
          onClick={() => setSelected(null)}
        >
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3>Record Detail</h3>
              <button className="ap-btn" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>

            <div className="ap-detail">
              <h4>Player / Record Info</h4>
              <div className="ap-kv">
                {Object.entries(selected.record || {}).map(([k, v]) => (
                  <div key={k} className="ap-kv-row">
                    <div className="ap-k">{k}</div>
                    <div className="ap-v">
                      {v === null || v === "" ? "-" : String(v)}
                    </div>
                  </div>
                ))}
              </div>

              <h4>Questions & Answers</h4>
              <ol className="ap-qa">
                {(selected.answers || []).map((a, idx) => (
                  <li key={idx} className="ap-qa-item">
                    <div className="ap-qa-q">
                      <span className="ap-badge">D{a.dimension}</span>{" "}
                      <span className="ap-muted">Q{a.questionOrder}</span>{" "}
                      {a.questionText}
                    </div>
                    <div className="ap-qa-a">
                      <span className="ap-muted">A{a.answerOrder}</span>{" "}
                      {a.answerText}{" "}
                      <span className="ap-score">(+{a.answerScore})</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      ) : null}
    </main>
  );
}

export default AdminPanel;
